import * as React from 'react'
import { FileImage, Printer, RefreshCw, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getOrders } from '@/api/orders'
import { apiClient } from '@/api/client'

type DocType = 'dispatch' | 'rental'

export default function DocumentTestPage() {
  const [orderNumber, setOrderNumber] = React.useState('')
  const [docType, setDocType] = React.useState<DocType>('dispatch')
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [resolvedId, setResolvedId] = React.useState<string | null>(null)

  // 最近訂單列表（快速選擇）
  const [recentOrders, setRecentOrders] = React.useState<Array<{ id: string; order_number: string; passenger_name: string }>>([])

  React.useEffect(() => {
    getOrders({ limit: 20 }).then(res => setRecentOrders(res.data || [])).catch(() => {})
  }, [])

  const handleGenerate = async (id?: string) => {
    const targetId = id ?? resolvedId
    if (!targetId && !orderNumber.trim()) return

    setLoading(true)
    setError(null)
    if (imageUrl) URL.revokeObjectURL(imageUrl)
    setImageUrl(null)

    try {
      let orderId = targetId

      // 如果沒有 id，先用 order_number 查
      if (!orderId) {
        const res = await getOrders({ limit: 1, search: orderNumber.trim() })
        const found = res.data?.find((o: any) =>
          o.order_number.toLowerCase() === orderNumber.trim().toLowerCase()
        )
        if (!found) {
          setError(`找不到訂單編號: ${orderNumber}`)
          return
        }
        orderId = found.id
        setResolvedId(found.id)
      }

      const endpoint = docType === 'dispatch'
        ? `/orders/${orderId}/generate-dispatch-document`
        : `/orders/${orderId}/generate-rental-document`

      const response = await apiClient.post(endpoint, {}, { responseType: 'blob' })
      setImageUrl(URL.createObjectURL(response.data))
    } catch (err: any) {
      const detail = err?.response?.data
      let msg = '生成失敗'
      if (detail instanceof Blob) {
        msg = await detail.text()
      } else if (typeof detail === 'object') {
        msg = detail?.detail || JSON.stringify(detail)
      }
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    if (!imageUrl) return
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<style>*{margin:0;padding:0;}body{background:#fff;}img{width:100%;display:block;}
@media print{@page{margin:0;}}</style>
</head><body><img src="${imageUrl}"/></body></html>`)
    win.document.close()
    win.focus()
    setTimeout(() => win.print(), 500)
  }

  const selectOrder = (id: string, num: string) => {
    setOrderNumber(num)
    setResolvedId(id)
    handleGenerate(id)
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/30">
          <FileImage className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">文件測試工具</h1>
      </div>

      {/* 控制區 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">

        {/* 單據類型 */}
        <div className="flex gap-2">
          {(['dispatch', 'rental'] as DocType[]).map(t => (
            <button
              key={t}
              onClick={() => { setDocType(t); setImageUrl(null); setError(null) }}
              className={`px-5 py-2 rounded-lg font-bold text-sm transition-colors cursor-pointer ${
                docType === t
                  ? t === 'dispatch'
                    ? 'bg-blue-600 text-white'
                    : 'bg-orange-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t === 'dispatch' ? '派車簽單' : '汽車出租單'}
            </button>
          ))}
        </div>

        {/* 訂單編號輸入 */}
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={orderNumber}
            onChange={e => { setOrderNumber(e.target.value); setResolvedId(null) }}
            onKeyDown={e => e.key === 'Enter' && handleGenerate()}
            placeholder="輸入訂單編號，例如 ORD20250315143022001"
            className="flex-1 h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm outline-none focus:border-blue-500"
          />
          <Button
            onClick={() => handleGenerate()}
            disabled={!orderNumber.trim() || loading}
            className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2"
          >
            <Search className="h-4 w-4" />
            {loading ? '生成中...' : '生成預覽'}
          </Button>
          {imageUrl && (
            <Button
              onClick={handlePrint}
              className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white gap-1"
            >
              <Printer className="h-4 w-4" />列印
            </Button>
          )}
        </div>

        {/* 快速選擇最近訂單 */}
        {recentOrders.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-2">最近訂單（點擊快速生成）：</p>
            <div className="flex flex-wrap gap-2">
              {recentOrders.map(o => (
                <button
                  key={o.id}
                  onClick={() => selectOrder(o.id, o.order_number)}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 transition-colors cursor-pointer text-left"
                >
                  <span className="font-mono font-bold text-gray-700 dark:text-gray-200">{o.order_number}</span>
                  <span className="text-gray-400 ml-1">{o.passenger_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 結果預覽 */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-32 gap-3 text-gray-400">
          <RefreshCw className="h-6 w-6 animate-spin" />
          <span>正在生成...</span>
        </div>
      )}

      {imageUrl && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              預覽 — {docType === 'dispatch' ? '派車簽單' : '汽車出租單'}（{orderNumber}）
            </span>
            <Button variant="outline" size="sm" onClick={() => handleGenerate()} className="gap-1 text-xs cursor-pointer">
              <RefreshCw className="h-3 w-3" />重新生成
            </Button>
          </div>
          <img
            src={imageUrl}
            alt="document preview"
            className="w-full h-auto rounded-lg shadow border border-gray-100 dark:border-gray-700"
          />
        </div>
      )}
    </div>
  )
}
